
import { useEffect, useState } from "react";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "./ui/dialog";
import { Card } from "./ui/card";
import { TablePagination } from "./ui/table-pagination";
import { db } from "../lib/supabase";
import { EvolutionChart } from "./EvolutionChart";
import { ChevronDown, ChevronUp } from "lucide-react";

interface MesRendimiento {
  id: string;
  period_month: string;
  total_acquisition_cost: number;
  total_asset_value: number;
  annual_rents: number;
  cash: number;
  equivalents: number;
  mortgages: number;
  bullet_loans: number;
  value: number;
}

interface PerformanceModalProps {
  open: boolean;
  onOpenChange: (v: boolean) => void;
}

export function PerformanceModal({ open, onOpenChange }: PerformanceModalProps) {
  const [meses, setMeses] = useState<MesRendimiento[]>([]);
  const [loading, setLoading] = useState(false);
  const [showChart, setShowChart] = useState(false);
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(10);

  const formatPeriodMonth = (isoDate: string | undefined) => {
    if (!isoDate) return "";
    const ym = String(isoDate).slice(0, 7);
    const [yyyy, mm] = ym.split("-");
    const year = Number(yyyy) || NaN;
    const monthIdx = Number(mm) ? Number(mm) - 1 : NaN;
    const monthNames = [
      "Enero",
      "Febrero",
      "Marzo",
      "Abril",
      "Mayo",
      "Junio",
      "Julio",
      "Agosto",
      "Septiembre",
      "Octubre",
      "Noviembre",
      "Diciembre",
    ];
    if (!Number.isFinite(year) || !Number.isFinite(monthIdx)) return "";
    return `${monthNames[monthIdx]} ${year}`;
  };

  const formatCurrency = (amount: number) => {
    return new Intl.NumberFormat("es-ES", {
      style: "currency",
      currency: "EUR",
      minimumFractionDigits: 2,
      maximumFractionDigits: 2,
    }).format(amount);
  };

  const fetchNavMonths = async () => {
    setLoading(true);
    try {
      const rows = await db.getNavMonths();
      const mapped: MesRendimiento[] = (rows || []).map((r: any) => ({
        id: r.id,
        period_month: r.period_start || r.period_month || "",
        value: Number(r.value) || 0,
        total_acquisition_cost: Number(r.total_acquisition_cost) || 0,
        total_asset_value: Number(r.total_asset_value) || 0,
        annual_rents: Number(r.annual_rents) || 0,
        cash: Number(r.cash) || 0,
        equivalents: Number(r.equivalents) || 0,
        mortgages: Number(r.mortgages) || 0,
        bullet_loans: Number(r.bullet_loans) || 0,
      }));

      mapped.sort((a, b) => (a.period_month > b.period_month ? 1 : a.period_month < b.period_month ? -1 : 0));
      setMeses(mapped);
    } catch (e) {
      // eslint-disable-next-line no-console
      console.error("fetchNavMonths failed", e);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (open) fetchNavMonths();
  }, [open]);

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-h-[92vh] w-full mx-auto overflow-y-auto text-gray-900 border border-gray-200 shadow-lg px-4 sm:px-6 overflow-x-hidden" style={{ backgroundColor: '#fafafa', boxSizing: 'border-box', maxWidth: 'min(1100px, calc(100vw - 2rem))', overflowX: 'hidden' }}>
        <DialogHeader>
          <DialogTitle className="text-2xl" style={{ fontWeight: 600 }}>
            Rendimiento (solo lectura)
          </DialogTitle>
        </DialogHeader>

        <div className="flex items-center justify-between mt-4">
          <div className="text-sm text-gray-600">Visualización del historial de rendimiento</div>
          <button
            type="button"
            className="inline-flex items-center gap-2 px-3 py-1.5 bg-white border border-gray-200 rounded-full text-sm hover:bg-gray-50"
            onClick={() => setShowChart((s) => !s)}
            aria-expanded={showChart}
          >
            {showChart ? (
              <><ChevronUp size={16} /> Ocultar gráfica</>
            ) : (
              <><ChevronDown size={16} /> Ver gráfica</>
            )}
          </button>
        </div>

        {/* Collapsible chart area */}
        <div className={`mt-4 transition-all duration-300 ${showChart ? 'max-h-[600px] opacity-100' : 'max-h-0 opacity-0 overflow-hidden'}`}>
          {showChart && <div className="p-4 bg-white rounded-lg border border-gray-100">
            <EvolutionChart meses={meses} />
          </div>}
        </div>

        <Card data-slot="card" className="bg-card text-card-foreground flex flex-col gap-6 rounded-xl border border-gray-200 shadow-sm w-full mt-6">
           <div className="performance-table-wrapper relative w-full">
              <style>{`
              /* Combined table styles (inspired by Fees.tsx) to ensure an internal horizontal
                 scroll and prevent the table from causing modal overflow. */

              .performance-table-wrapper { overflow-x: auto; -webkit-overflow-scrolling: touch; width: 100%; max-width: 100%; box-sizing: border-box; padding: 0; display: block; }

              /* Allow the table to grow horizontally while the wrapper provides an internal scrollbar. */
              .performance-table { display: inline-table; width: max-content; min-width: 0; table-layout: auto; border-collapse: collapse; }

              /* Keep single-line cells by default so horizontal scrolling works predictably */
              .performance-table th, .performance-table td { box-sizing: border-box; white-space: nowrap; padding-left: 0.75rem; padding-right: 0.75rem; text-overflow: clip; }
              .performance-table th:last-child, .performance-table td:last-child { padding-right: 1.5rem; }

              /* Inner element that expands with the table so the background covers the full scroll width */
              .performance-table-inner { display: inline-block; min-width: 100%; box-sizing: border-box; background-clip: padding-box; }

              /* Give the wrapper a bottom padding so the small-screen scroll hint doesn't overlap table content */
              .performance-table-wrapper { padding-bottom: 1.25rem; }

              /* On very narrow screens allow wrapping to avoid extreme horizontal growth */
              @media (max-width: 480px) { .performance-table th, .performance-table td { white-space: normal; } }

              /* On larger screens make the table take full width and behave responsively */
              @media (min-width: 768px) {
                .performance-table-wrapper { overflow-x: hidden; }
                .performance-table { display: table; width: 100%; min-width: 0; table-layout: fixed; }
                .performance-table th, .performance-table td { white-space: nowrap; overflow: hidden; text-overflow: ellipsis; }
                /* Column width hints for desktop layout */
                .performance-table col.col-month { width: 18%; }
                .performance-table col.col-value { width: 10%; }
                .performance-table col.col-acq { width: 14%; }
                .performance-table col.col-asset { width: 14%; }
                .performance-table col.col-rents { width: 10%; }
                .performance-table col.col-cash { width: 8%; }
                .performance-table col.col-eq { width: 8%; }
                .performance-table col.col-mort { width: 9%; }
                .performance-table col.col-bullet { width: 9%; }
              }

              /* visual hint container (non-interactive) */
              .performance-scroll-hint { position: absolute; right: 0.75rem; bottom: 0.5rem; pointer-events: none; }

              /* Ensure the outer Card slot respects dialog padding and doesn't expand beyond modal */
              [data-slot="card"] { box-sizing: border-box; max-width: 100%; }
              [data-slot="card"] .performance-table-wrapper { box-sizing: border-box; }

              /* Row hover effect similar to Fees styling */
              .performance-table tbody tr:hover td { background-color: #F9FAFB; transition: background-color 120ms ease; }
            `}</style>

            <div className="performance-table-inner rounded-lg bg-white">
              <table className="performance-table table-auto divide-y divide-gray-200">
                <colgroup className="performance-colgroup">
                  <col className="col-month" />
                  <col className="col-value" />
                  <col className="col-acq" />
                  <col className="col-asset" />
                  <col className="col-rents" />
                  <col className="col-cash" />
                  <col className="col-eq" />
                  <col className="col-mort" />
                  <col className="col-bullet" />
                </colgroup>
                <thead>
                  <tr className="border-b border-gray-200 bg-gray-50">
                    <th className="text-left px-4 py-4 text-xs text-gray-600 uppercase" style={{ fontWeight: 600 }}>Mes</th>
                    <th className="text-left px-4 py-4 text-xs text-gray-600 uppercase" style={{ fontWeight: 600 }}>Value</th>
                    <th className="text-left px-4 py-4 text-xs text-gray-600 uppercase" style={{ fontWeight: 600 }}>Total Acquisition Cost</th>
                    <th className="text-left px-4 py-4 text-xs text-gray-600 uppercase" style={{ fontWeight: 600 }}>Total Asset Value</th>
                    <th className="text-left px-4 py-4 text-xs text-gray-600 uppercase" style={{ fontWeight: 600 }}>90% Annual Rents</th>
                    <th className="text-left px-4 py-4 text-xs text-gray-600 uppercase" style={{ fontWeight: 600 }}>Cash</th>
                    <th className="text-left px-4 py-4 text-xs text-gray-600 uppercase" style={{ fontWeight: 600 }}>Equivalents</th>
                    <th className="text-left px-4 py-4 text-xs text-gray-600 uppercase" style={{ fontWeight: 600 }}>Mortgages</th>
                    <th className="text-left px-4 py-4 text-xs text-gray-600 uppercase" style={{ fontWeight: 600 }}>Bullet Loans</th>
                  </tr>
                </thead>

                <tbody className="divide-y divide-gray-200">
                  {loading
                    ? Array.from({ length: 8 }).map((_, i) => (
                        <tr key={`skeleton-${i}`} className="animate-pulse">
                          <td className="px-4 py-4"><div className="h-4 bg-gray-200 rounded w-24"/></td>
                          <td className="px-4 py-4"><div className="h-4 bg-gray-200 rounded w-20"/></td>
                          <td className="px-4 py-4"><div className="h-4 bg-gray-200 rounded w-28"/></td>
                          <td className="px-4 py-4"><div className="h-4 bg-gray-200 rounded w-28"/></td>
                          <td className="px-4 py-4"><div className="h-4 bg-gray-200 rounded w-24"/></td>
                          <td className="px-4 py-4"><div className="h-4 bg-gray-200 rounded w-20"/></td>
                          <td className="px-4 py-4"><div className="h-4 bg-gray-200 rounded w-24"/></td>
                          <td className="px-4 py-4"><div className="h-4 bg-gray-200 rounded w-24"/></td>
                          <td className="px-4 py-4"><div className="h-4 bg-gray-200 rounded w-28"/></td>
                        </tr>
                      ))
                    : meses
                        .slice()
                        .sort((a, b) => (b.period_month || "").localeCompare(a.period_month || ""))
                        .slice((page - 1) * pageSize, (page - 1) * pageSize + pageSize)
                        .map((mes) => (
                          <tr key={mes.id} className="hover:bg-gray-50 transition-colors">
                            <td className="px-4 py-4 whitespace-nowrap truncate max-w-[140px]"><div className="text-sm text-[#0F172A]" style={{ fontWeight: 500 }}>{formatPeriodMonth(mes.period_month)}</div></td>
                            <td className="px-4 py-4 whitespace-nowrap text-right md:text-left"><div className="text-sm text-gray-900">{mes.value > 0 ? formatCurrency(mes.value) : "-"}</div></td>
                            <td className="px-4 py-4"><div className="text-sm text-gray-900">{mes.total_acquisition_cost > 0 ? formatCurrency(mes.total_acquisition_cost) : "-"}</div></td>
                            <td className="px-4 py-4"><div className="text-sm text-gray-900">{mes.total_asset_value > 0 ? formatCurrency(mes.total_asset_value) : "-"}</div></td>
                            <td className="px-4 py-4"><div className="text-sm text-gray-900">{mes.annual_rents > 0 ? mes.annual_rents.toLocaleString('es-ES') : "-"}</div></td>
                            <td className="px-4 py-4"><div className="text-sm text-gray-900">{mes.cash > 0 ? formatCurrency(mes.cash) : "-"}</div></td>
                            <td className="px-4 py-4"><div className="text-sm text-gray-900">{mes.equivalents > 0 ? formatCurrency(mes.equivalents) : "-"}</div></td>
                            <td className="px-4 py-4"><div className="text-sm text-gray-900">{mes.mortgages > 0 ? formatCurrency(mes.mortgages) : "-"}</div></td>
                            <td className="px-4 py-4"><div className="text-sm text-gray-900">{mes.bullet_loans > 0 ? formatCurrency(mes.bullet_loans) : "-"}</div></td>
                          </tr>
                        ))}
                </tbody>
              </table>
            </div>

            {/* Scroll hint for small screens */}
            <div className="sm:hidden performance-scroll-hint">
              <div className="flex items-center gap-2 text-xs text-gray-600 pr-2">
                <span>Desliza</span>
                <svg width="18" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" className="text-gray-500">
                  <path d="M3 12h18"></path>
                  <path d="M15 6l6 6-6 6"></path>
                </svg>
              </div>
            </div>
            </div>
        </Card>

        <div className="flex justify-end mt-4">
          <TablePagination
            page={page}
            totalPages={Math.max(1, Math.ceil(meses.length / pageSize))}
            pageSize={pageSize}
            onPrev={() => setPage((p) => Math.max(1, p - 1))}
            onNext={() => setPage((p) => Math.min(Math.max(1, Math.ceil(meses.length / pageSize)), p + 1))}
            onPageSizeChange={(s) => { setPageSize(s); setPage(1); }}
          />
        </div>
      </DialogContent>
    </Dialog>
  );
}
